import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Alert,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import ArticleIcon from '@mui/icons-material/Article';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { documentsAPI } from '../../services/api';
import { toast } from 'react-toastify';

export interface DocumentPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  documentId: string | null;
  documentTitle?: string;
  documentFilename?: string;
  documents?: any[]; // Optional array for navigation
  currentIndex?: number; // Current document index for navigation
  onNavigate?: (index: number) => void; // Callback for navigation
  personName?: string; // Optional person name for avatar
  personPhotoUrl?: string; // Optional person photo URL
}

const DocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({
  open,
  onClose,
  documentId,
  documentTitle,
  documentFilename,
  documents,
  currentIndex,
  onNavigate,
  personName,
  personPhotoUrl,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string>('');
  const [textContent, setTextContent] = useState<string | null>(null);
  const [document, setDocument] = useState<any>(null);

  const loadDocument = useCallback(async () => {
    if (!documentId) return;

    try {
      setLoading(true);
      setError(null);
      setTextContent(null);

      // Fetch document metadata
      const metaResponse = await documentsAPI.getById(documentId);
      const docMeta = metaResponse.data.data || metaResponse.data; // Handle nested data structure
      setDocument(docMeta);

      // Get file extension - try multiple fields
      const filename = docMeta.filename || documentFilename || docMeta.originalFilename || docMeta.name || '';
      let ext = filename.split('.').pop()?.toLowerCase() || '';

      // Fallback: try to detect from MIME type if no extension
      if (!ext && docMeta.mimeType) {
        const mimeMap: Record<string, string> = {
          'application/pdf': 'pdf',
          'image/jpeg': 'jpg',
          'image/png': 'png',
          'image/gif': 'gif',
          'text/plain': 'txt',
        };
        ext = mimeMap[docMeta.mimeType] || '';
      }

      // Fallback: check documentType field
      if (!ext && docMeta.documentType) {
        const type = docMeta.documentType.toLowerCase();
        if (type.includes('pdf') || type === 'incorporation' || type === 'filing') {
          ext = 'pdf';
        }
      }

      setFileType(ext);

      // Download document blob
      const response = await documentsAPI.download(documentId);

      // Create blob with proper MIME type
      let mimeType = 'application/octet-stream';
      if (ext === 'pdf') {
        mimeType = 'application/pdf';
      } else if (isImageFile(ext)) {
        const imageTypes: Record<string, string> = {
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'bmp': 'image/bmp',
          'webp': 'image/webp',
          'svg': 'image/svg+xml',
        };
        mimeType = imageTypes[ext] || 'image/jpeg';
      } else if (isTextFile(ext)) {
        mimeType = 'text/plain';
      }

      const blob = new Blob([response.data], { type: mimeType });

      // Determine how to preview based on file type
      if (isImageFile(ext)) {
        // Image: create object URL
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } else if (ext === 'pdf') {
        // PDF: create object URL with proper type
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } else if (isTextFile(ext)) {
        // Text files: read as text
        const text = await blob.text();
        setTextContent(text);
      } else {
        // Other files: show download option only
        setPreviewUrl(null);
      }
    } catch (e: any) {
      console.error('Failed to load document:', e);
      setError('Failed to load document preview');
      toast.error('Failed to load document preview');
    } finally {
      setLoading(false);
    }
  }, [documentFilename, documentId]);

  useEffect(() => {
    if (open && documentId) {
      loadDocument();
    } else {
      // Reset state when dialog closes
      setPreviewUrl(null);
      setTextContent(null);
      setError(null);
      setFileType('');
      setDocument(null);
    }
  }, [documentId, loadDocument, open]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const isImageFile = (ext: string) => {
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
  };

  const isTextFile = (ext: string) => {
    return ['txt', 'json', 'xml', 'csv', 'md', 'log', 'js', 'ts', 'jsx', 'tsx', 'css', 'html', 'yaml', 'yml'].includes(ext);
  };

  const isOfficeFile = (ext: string) => {
    return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
  };

  const handleDownload = async () => {
    if (!documentId) return;

    try {
      const response = await documentsAPI.download(documentId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', document?.filename || documentFilename || 'download');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded');
    } catch (e: any) {
      toast.error('Failed to download document');
      console.error(e);
    }
  };

  const handleOpenInNewTab = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };

  const handleNavigate = (direction: 'prev' | 'next') => {
    if (!documents || currentIndex === undefined || !onNavigate) return;

    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < documents.length) {
      onNavigate(newIndex);
    }
  };

  const getFileIcon = () => {
    if (isImageFile(fileType)) return <ImageIcon sx={{ fontSize: 48, color: '#9c27b0' }} />;
    if (fileType === 'pdf') return <PictureAsPdfIcon sx={{ fontSize: 48, color: '#d32f2f' }} />;
    if (isTextFile(fileType)) return <ArticleIcon sx={{ fontSize: 48, color: '#2196f3' }} />;
    return <InsertDriveFileIcon sx={{ fontSize: 48, color: '#9e9e9e' }} />;
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const canShowNavigation = documents && documents.length > 1 && currentIndex !== undefined && onNavigate;
  const canGoPrev = canShowNavigation && currentIndex > 0;
  const canGoNext = canShowNavigation && currentIndex < documents.length - 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2} flex={1} minWidth={0}>
            {getFileIcon()}
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" noWrap>
                {documentTitle || document?.title || 'Document Preview'}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  {document?.filename || documentFilename || 'Unknown'}
                </Typography>
                {document?.fileSize && (
                  <>
                    <Typography variant="caption" color="text.secondary">•</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(document.fileSize)}
                    </Typography>
                  </>
                )}
                {document?.documentType && (
                  <Chip
                    label={document.documentType.replace(/_/g, ' ')}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20 }}
                  />
                )}
              </Stack>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close document preview">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            gap={2}
          >
            <CircularProgress size={48} />
            <Typography variant="body2" color="text.secondary">
              Loading preview...
            </Typography>
          </Box>
        ) : error ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            gap={2}
            p={4}
          >
            <Alert severity="error" sx={{ maxWidth: 500 }}>
              {error}
            </Alert>
          </Box>
        ) : previewUrl && isImageFile(fileType) ? (
          // Image Preview
          <Box
            flex={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ bgcolor: 'grey.100', p: 2, overflow: 'auto' }}
          >
            <img
              src={previewUrl}
              alt={documentTitle || document?.title || 'Preview'}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        ) : previewUrl && fileType === 'pdf' ? (
          // PDF Preview
          <Box flex={1} sx={{ overflow: 'hidden' }}>
            <iframe
              src={previewUrl}
              title={documentTitle || document?.title || 'PDF Preview'}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </Box>
        ) : textContent ? (
          // Text Preview
          <Box flex={1} sx={{ overflow: 'auto', p: 3, bgcolor: 'grey.50' }}>
            <Box
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                m: 0,
              }}
            >
              {textContent}
            </Box>
          </Box>
        ) : isOfficeFile(fileType) ? (
          // Office Files - No preview, show download message
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            gap={2}
            p={4}
          >
            {getFileIcon()}
            <Typography variant="h6" color="text.secondary">
              Preview not available for Office documents
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Microsoft Office files cannot be previewed in the browser.
              <br />
              Please download the file to view it.
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              size="large"
            >
              Download Document
            </Button>
          </Box>
        ) : (
          // Other files - show file info and download option
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            gap={2}
            p={4}
          >
            {getFileIcon()}
            <Typography variant="h6" color="text.secondary">
              Preview not available
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              This file type{fileType && ` (.${fileType})`} cannot be previewed in the browser.
              <br />
              Please download the file to view it.
            </Typography>

            {document?.description && (
              <Alert severity="info" sx={{ maxWidth: 500 }}>
                {document.description}
              </Alert>
            )}
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              size="large"
            >
              Download Document
            </Button>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        {/* Navigation buttons */}
        <Box>
          {canShowNavigation && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<NavigateBeforeIcon />}
                onClick={() => handleNavigate('prev')}
                disabled={!canGoPrev}
              >
                Previous
              </Button>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                {(currentIndex || 0) + 1} / {documents?.length || 0}
              </Typography>
              <Button
                size="small"
                endIcon={<NavigateNextIcon />}
                onClick={() => handleNavigate('next')}
                disabled={!canGoNext}
              >
                Next
              </Button>
            </Stack>
          )}
        </Box>

        {/* Action buttons */}
        <Stack direction="row" spacing={1}>
          {(previewUrl || textContent) && (
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenInNewTab}
              disabled={!previewUrl}
            >
              Open in New Tab
            </Button>
          )}
          <Button startIcon={<DownloadIcon />} onClick={handleDownload}>
            Download
          </Button>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
